import type { CurrencyType } from "types/BetTypes";
import {
  message,
  watch,
  type Result,
  ResultError,
  type MessageError,
} from "utilities/message/message";
import { convertAmountToCents } from "common/currency";
import { convertErrorToToast } from "utilities/errorsParser";
import { toast } from "@picklebet/ui";
import { getBridge } from "utilities/bridge";
import { isWebView } from "utilities/display";
import { MW_3DS_FRAME_ID } from "../components/Mw3dsFrame";
import { telemetry } from "@/utilities/telemetry";
import { parsePhoneNumber } from "react-phone-number-input";
import { useWalletStore } from "../stores/walletStore";
import {
  err,
  ok,
  type Result as ResultType,
  type ResultAsync,
} from "utilities/result";
import { useAuthStore } from "@/features/auth/stores/authStore";
import type { Maybe } from "@/types/utilities";

export type DepositOutcome = "success" | "not-enrolled";

const beautifyMerchantWarriorError = (message: string) => {
  if (message.includes("MW - 007")) {
    return "You've entered an invalid card number";
  }
  if (message.includes("MW - 068")) {
    return "Credit cards are prohibited by law. Please add a debit card, or deposit with PayID";
  }
  return "Something went wrong, please contact support";
};

export const handle3dsChallengeIframe = (creq: string, acsUrl: string) => {
  const form = document.createElement("form");
  form.setAttribute("method", "POST");
  form.setAttribute("action", acsUrl);
  form.setAttribute("target", MW_3DS_FRAME_ID);
  form.setAttribute("sandbox", "allow-forms allow-same-origin allow-scripts");

  const creqInputField = document.createElement("input");
  creqInputField.setAttribute("type", "hidden");
  creqInputField.setAttribute("name", "creq");
  creqInputField.setAttribute("value", creq);
  form.appendChild(creqInputField);
  document.body.appendChild(form);
  form.submit();

  telemetry.threeDsChallengeRequestSent();
};

export const handlePostMwFingerPrintIframe = (
  paReq: string,
  acsUrl: string,
) => {
  const form = document.createElement("form");
  form.setAttribute("method", "POST");
  form.setAttribute("action", acsUrl);
  form.setAttribute("target", MW_3DS_FRAME_ID);
  form.setAttribute("sandbox", "allow-forms allow-same-origin allow-scripts");

  const inputField = document.createElement("input");
  inputField.setAttribute("type", "hidden");
  inputField.setAttribute("name", "threeDSMethodData");
  inputField.setAttribute("value", paReq);
  form.appendChild(inputField);
  document.body.appendChild(form);
  form.submit();

  telemetry.threeDsFingerprintRequestSent();
};

const post3dsChallenge = (creq: string, acsUrl: string, userId: string) => {
  if (isWebView()) {
    const payload = {
      ["3DS Challenge Request Sent"]: {
        userId,
        path: window?.location?.pathname,
        title: document.title,
        creq,
        acsUrl,
      },
    };
    getBridge().send.threeDSChallenge(payload);
    return;
  }
  handle3dsChallengeIframe(creq, acsUrl);
};

const postMwFingerPrint = (paReq: string, acsUrl: string, userId: string) => {
  if (isWebView()) {
    const payload = {
      ["3DS Fingerprint Request Sent"]: {
        userId,
        path: window?.location?.pathname,
        title: document.title,
        paReq,
        acsUrl,
      },
    };
    getBridge().send.threeDSFingerprint(payload);
    return;
  }
  handlePostMwFingerPrintIframe(paReq, acsUrl);
};

const getBrowserInfo = () => {
  if (
    typeof window === "undefined" ||
    typeof navigator === "undefined" ||
    typeof screen === "undefined"
  ) {
    return null;
  }
  return {
    browserJavascriptEnabled: true,
    browserJavaEnabled: navigator.javaEnabled(),
    browserColorDepth: screen.colorDepth.toString(),
    browserLanguage: navigator.language || (navigator as any).userLanguage,
    browserScreenHeight: screen.height.toString(),
    browserScreenWidth: screen.width.toString(),
    browserTZ: new Date().getTimezoneOffset().toString(),
    browserUserAgent: navigator.userAgent,
  };
};

// Deposit Services

type FirstTimeDepositParams = {
  currency: string;
  method: string;
  amount?: number;
};

const firstTimeDeposit = ({
  currency,
  method,
  amount = 0,
}: FirstTimeDepositParams) => {
  telemetry.firstTimeDepositFe({
    currency,
    method,
    amount,
    value: amount,
  });
};

const skrillDeposit = async (
  amount: number,
  promotionCode: Maybe<string>,
  currency: string | CurrencyType,
) => {
  const depositCents = convertAmountToCents(amount);

  const returnUrl = new URL(window.location.href);
  returnUrl.searchParams.set("return", "true");
  returnUrl.searchParams.set("method", "skrill");
  returnUrl.searchParams.set("transactionId", "{transactionId}");

  const cancelUrl = new URL(window.location.href);
  cancelUrl.searchParams.set("cancel", "true");
  cancelUrl.searchParams.set("method", "skrill");
  cancelUrl.searchParams.set("transactionId", "{transactionId}");

  const [response, error] = await message("skrill-deposit", {
    amount: depositCents,
    returnUrl: returnUrl.toString(),
    returnUrlText: "Back to Picklebet",
    cancelUrl: cancelUrl.toString(),
    logoUrl: new URL("/images/logo-small.png", window.location.href).toString(),
  });

  const sid = response?.data?.sid;

  if (error || !sid) {
    telemetry.depositedCashFailure({
      currency: String(currency),
      method: "skrill",
      amount: depositCents / 100,
      outcome: "failure",
      value: depositCents / 100,
      fingerprintIssued: false,
      challengeIssued: false,
    });
    return err(error ?? new Error("Unable to deposit funds"));
  }

  telemetry.depositedCash({
    currency: String(currency),
    method: "skrill",
    amount: depositCents / 100,
    outcome: "success",
    promotion: promotionCode ? "Yes" : "No",
    promotionCode: promotionCode,
    value: depositCents / 100,
    fingerprintIssued: false,
    challengeIssued: false,
  });

  return ok({ sid });
};

type MWDepositParams = {
  cardId?: string | null;
  csc?: string;
  digitalWalletToken?: string | null;
  amount: string | number;
  promotionCode?: string;
  digitalWalletType?: "Google" | "Apple";
  panSuffix?: string;
};

const merchantWarriorDeposit = async ({
  cardId,
  csc,
  digitalWalletToken = null,
  amount,
  promotionCode = "",
  digitalWalletType,
  panSuffix,
}: MWDepositParams): ResultAsync<DepositOutcome, ResultError | Error> => {
  const authState = useAuthStore.getState();
  const userId =
    authState.status === "authenticated" || authState.status === "impersonating"
      ? authState.session.userId
      : "";
  const depositCents = convertAmountToCents(amount);
  let fingerprintIssued = false;
  let challengeIssued = false;
  const store = useWalletStore.getState();

  window.addEventListener(
    "message",
    (event) => {
      if (event.data?.type === "mw-challenge" && event.data?.done) {
        store.closeManual3ds();
      }
    },
    false,
  );

  let payload;

  if (cardId) {
    payload = {
      amount: depositCents,
      csc,
      cardId,
      browserInfo: getBrowserInfo(),
    };
  } else if (digitalWalletToken) {
    payload = {
      amount: depositCents,
      digitalWalletToken,
      digitalWalletType,
      browserInfo: getBrowserInfo(),
      panSuffix,
    };
  }

  if (!payload) return err(new Error("Unable to deposit funds!"));

  const [watcher, error] = await watch("mw-deposit-funds-3ds", payload, {
    timeOutMs: 120 * 1000,
  });

  if (!watcher) return err(error ?? new Error("Unable to deposit funds!"));

  function trackFailure(error: ResultError | Error) {
    telemetry.depositedCashFailure({
      currency: "AUD",
      method: "card",
      amount: depositCents / 100,
      outcome: "failure",
      fingerprintIssued,
      challengeIssued,
      code: error instanceof ResultError ? error?.code : "",
      value: depositCents / 100,
    });
  }

  async function handleResponse(
    result: Result,
  ): ResultAsync<DepositOutcome, ResultError | Error> {
    const [response, error] = result;

    if (error) {
      trackFailure(error);
      store.closeManual3ds();

      if (error.code === "threeds-enrollment-failed") {
        return ok("not-enrolled");
      }
      return err(error);
    }

    if (!response || !watcher) {
      return err(new Error("Unable to deposit funds!"));
    }

    switch (response.code) {
      case "3ds-fingerprint-required": {
        telemetry.threeDsFingerprintRequired();
        fingerprintIssued = true;

        postMwFingerPrint(
          response?.data?.["pa-req"],
          response?.data?.["acs-url"],
          userId,
        );
        const fingerPrintResponse = await watcher.next();
        return await handleResponse(fingerPrintResponse);
      }
      case "3ds-challenge-required": {
        telemetry.threeDsChallengeRequired();
        challengeIssued = true;

        post3dsChallenge(
          response?.data?.["challenge-data"],
          response?.data?.["acs-url"],
          userId,
        );

        const iframe = document.getElementById(
          MW_3DS_FRAME_ID,
        ) as HTMLIFrameElement;

        iframe.onload = () => {
          store.openManual3ds();
          telemetry.threeDsChallenge();
          iframe.onload = null;
        };

        const challengeResponse = await watcher.next();
        return await handleResponse(challengeResponse);
      }
      case "funds-deposited": {
        telemetry.depositedCash({
          currency: "AUD",
          method: "card",
          amount: depositCents / 100,
          outcome: "success",
          promotion: promotionCode ? "Yes" : "No",
          promotionCode: promotionCode,
          fingerprintIssued,
          challengeIssued,
          value: depositCents / 100,
        });
        if (String(response.data.firstTime) === "true") {
          telemetry.firstTimeDepositFe({
            currency: "AUD",
            method: "card",
            amount: depositCents / 100,
            value: depositCents / 100,
          });
        }
        store.closeManual3ds();
        return ok("success");
      }
      default: {
        const displayError = new Error(response.displayText);
        trackFailure(displayError);
        return err(displayError);
      }
    }
  }

  if (error) {
    trackFailure(error);
    return err(error);
  }

  const response = await watcher.next();
  return await handleResponse(response);
};

const generatePayID = async (): ResultAsync<"success", MessageError> => {
  const [, error] = await message("generate-user-pay-id", {});

  if (error) {
    return err(error);
  }

  return ok("success");
};

const interacDeposit = async (amount: number) => {
  const depositCents = convertAmountToCents(amount);

  const [response, error] = await message("gigadat-interac-deposit", {
    amount: depositCents,
    provider: "GIGADAT_INTERAC",
    verification: false,
  });

  if (error) {
    return err(error);
  }

  const paymentToken = response?.data?.paymentToken;
  const redirectUrl = response?.data?.redirectUrl;

  if (!paymentToken || !redirectUrl) {
    return err(new Error("Unable to deposit funds"));
  }

  return ok({ redirectUrl, paymentToken });
};

let addedCardId: string;

type AddCardData = {
  name: string;
  number: string;
  expiry: string;
};

const addCard = async (
  data: AddCardData,
  callback?: (addedCardId: string) => void,
  onCardAdded?: (cardId: string) => void,
): Promise<ResultType<string>> => {
  const expiryMonth = data.expiry.slice(0, 2);
  const expiryYear = data.expiry.slice(-2);

  const [response, error] = await message("mw-get-add-card-access-token", {
    numberFirst: data.number.slice(0, 4),
    numberLast: data.number.slice(-4),
    name: data.name,
    expiryMonth,
    expiryYear,
    returnUrl: "",
  });

  addedCardId = response?.data?.custom1;

  if (error) {
    showCardError(error?.message);
    return err(error);
  }

  if (!response) return err(new Error("No response"));

  const mwClient = await window.MerchantWarrior.createWithToken(
    response.data?.accessToken,
    {},
  );

  mwClient.directRequest({
    method: "addCard",
    cardName: data.name,
    cardNumber: data.number,
    cardExpiryMonth: expiryMonth,
    cardExpiryYear: expiryYear,
  });

  const cardAddedPromise: Promise<{ cardID: string }> = new Promise(
    (resolve, reject) => {
      mwClient.on(
        "card-added",
        (isSuccess: boolean, response: { cardID: string }) => {
          if (isSuccess) {
            resolve(response);
          } else {
            reject(response);
          }
        },
      );
    },
  );

  try {
    const cardResponse = await cardAddedPromise;
    await confirmCard(cardResponse?.cardID, () => onCardAdded?.(addedCardId));
  } catch (cardError: any) {
    showCardError(cardError?.responseMessage);
    return err(cardError);
  }

  callback?.(addedCardId);
  return ok(addedCardId);
};

const confirmCard = async (cardId: string, onConfirm?: () => void) => {
  const [, error] = await message("mw-confirm-card", { cardId });

  if (error) {
    console.error("Confirm Card Error", error);
    toast(convertErrorToToast(error, { title: "Unable to add a card!" }));
    telemetry.addedCardCashDeposit({ outcome: "failure" });
    return err(error);
  }

  telemetry.addedCardCashDeposit({ outcome: "success" });
  onConfirm?.();
  return ok(true);
};

const showCardError = (message: string) => {
  toast({
    title: "Unable to add a card!",
    variant: "danger",
    description: beautifyMerchantWarriorError(message),
  });
  telemetry.addedCardCashDeposit({ outcome: "failure" });
};

const verifyCard = async (id: string, code: string) => {
  const [response, error] = await message("verify-card", { code, id });

  if (error) {
    toast(convertErrorToToast(error, { title: "Unable to verify card!" }));
    return err(error);
  }

  if (!response) return err(new Error("No response"));

  toast({
    title: "Success!",
    variant: "success",
    description: response.displayText,
  });

  return ok(response);
};

const beginVerification = async (id: string, callback?: () => void) => {
  const [response, error] = await message("mw-begin-verification", { id });

  if (error) {
    toast(convertErrorToToast(error, { title: "Unable to verify!" }));
    return err(error);
  }

  if (!response) return err(new Error("No response"));

  toast({
    title: "Success!",
    variant: "success",
    description: response.displayText,
  });

  callback?.();
  return ok(response);
};

const removeCard = async (id: string) => {
  const [, error] = await message("mw-remove-card", { id });

  if (error) {
    console.error("Error removing card", error);
    return err(error);
  }

  telemetry.removedCardCashDeposit();
  return ok(true);
};

const addBank = async (name: string, bsb: string, number: string) => {
  const [, error] = await message("create-au-bank-account", {
    name,
    bsb,
    number,
  });

  if (error) {
    telemetry.addedBankDetails({ outcome: "failure" });
    return err(error);
  }

  telemetry.addedBankDetails({ outcome: "success" });
  toast({
    title: "Success!",
    variant: "success",
    description: "Your bank details were added successfully.",
  });

  return ok(true);
};

const removeBankAccount = async (callback?: () => void) => {
  const [, error] = await message("remove-bank-account");

  if (error) {
    telemetry.removedBankAccount({ outcome: "failure" });
    return err(error);
  }

  telemetry.removedBankAccount({ outcome: "success" });
  callback?.();

  return ok(true);
};

type AUDBankWithdrawPayload = {
  amount: number;
  currency: string;
  bankAccountId: string;
  externalType: "AUD_BANK_ACCOUNT";
  userExternalWalletId?: string;
  verificationRequestId?: string;
};

type SkrillWithdrawPayload = {
  amount: number;
  currency: string;
  skrillEmail: string;
  externalType: "SKRILL_ACCOUNT";
};

type GigadatInteracWithdrawPayload = {
  amount: number;
  currency: string;
  externalType: "GIGADAT_INTERAC";
  userExternalWalletId?: string;
};

type WithdrawPayload =
  | AUDBankWithdrawPayload
  | SkrillWithdrawPayload
  | GigadatInteracWithdrawPayload;

const withdrawCash = async (payload: WithdrawPayload) => {
  const [, error] = await message("create-withdrawal", payload);

  if (error) {
    telemetry.withdrewCash({
      currency: payload.currency,
      amount: payload.amount / 100,
      outcome: "failure",
      value: payload.amount / 100,
    });
    return err(error);
  }

  telemetry.withdrewCash({
    currency: payload.currency,
    amount: payload.amount / 100,
    outcome: "success",
    value: payload.amount / 100,
  });

  return ok(true);
};

type CancelWithdrawalParams = {
  id: string;
  withdrawal: { amount: number; currency: string };
};

const cancelWithdrawal = async (
  { id, withdrawal }: CancelWithdrawalParams,
  callback?: () => void,
) => {
  const [, error] = await message("cancel-withdrawal", id);

  if (error) {
    toast(
      convertErrorToToast(error, { title: "Unable to cancel withdrawal!" }),
    );
    return err(error);
  }

  telemetry.cancelWithdrawal({
    amount: withdrawal.amount / 100,
    currency: withdrawal.currency,
    value: withdrawal.amount / 100,
  });

  toast({
    title: "Success!",
    variant: "success",
    description: "Your withdrawal was cancelled.",
  });

  callback?.();
  return ok(true);
};

const addInteracBankAccount = async ({
  email,
  phone,
}: {
  email: string;
  phone: string;
}) => {
  const [res, error] = await message("gigadat-interac-add-wallet", {
    email,
    mobile: parsePhoneNumber(phone)?.nationalNumber ?? phone,
  });

  if (error) {
    return err(error);
  }

  toast({
    variant: "success",
    title: "Success",
    description: res?.displayText,
  });

  return ok(res);
};

const removeInteracBankAccount = async (id: string) => {
  const [, error] = await message("gigadat-interac-remove-wallet", { id });

  if (error) {
    toast(
      convertErrorToToast(error, { title: "Unable to remove Interac details" }),
    );
    return err(error);
  }

  toast({
    variant: "success",
    title: "Success",
    description: "Your Interac details were removed.",
  });

  return ok(true);
};

const setDefaultInteracBankAccount = async (walletId: string) => {
  const [_, error] = await message("gigadat-interac-set-default-wallet", {
    walletId,
  });

  if (error) {
    return err(error);
  }

  return ok(true);
};

export const walletService = {
  // Deposits
  skrillDeposit,
  merchantWarriorDeposit,
  interacDeposit,
  generatePayID,
  firstTimeDeposit,
  // Cards
  addCard,
  verifyCard,
  beginVerification,
  removeCard,
  // Bank
  addBank,
  removeBankAccount,
  // Withdrawals
  withdrawCash,
  cancelWithdrawal,
  addInteracBankAccount,
  removeInteracBankAccount,
  setDefaultInteracBankAccount,
};

export type {
  MWDepositParams,
  AddCardData,
  WithdrawPayload,
  AUDBankWithdrawPayload,
  SkrillWithdrawPayload,
  GigadatInteracWithdrawPayload,
  CancelWithdrawalParams,
};
